import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Users, Bed, Bath, Car, Wifi, Utensils, Tv, Wind, Waves } from 'lucide-react';
import { villaImages } from '../data/images';
import { Helmet } from 'react-helmet';


const Villa: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState<{[key: string]: boolean}>({});

  // Fallback images from Pexels
  const fallbackImages = [
    // 'https://images.pexels.com/photos/1743231/pexels-photo-1743231.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1648776/pexels-photo-1648776.jpeg?auto=compress&cs=tinysrgb&w=1200',
    // 'https://images.pexels.com/photos/1571463/pexels-photo-1571463.jpeg?auto=compress&cs=tinysrgb&w=1200'
  ];

  const amenities = [
    { icon: Users, text: 'Sleeps up to 8 guests' },
    { icon: Bed, text: '3 spacious bedrooms' },
    { icon: Bath, text: '2 full bathrooms' },
    { icon: Waves, text: 'Swimming Pool complete with a Jacuzzi' },
    { icon: Car, text: 'Private parking for 4 cars' },
    { icon: Wifi, text: 'High-speed WiFi' },
    { icon: Utensils, text: 'Fully equipped kitchen' },
    { icon: Tv, text: 'Smart TV with streaming' },
    { icon: Wind, text: 'Air conditioning throughout' }
  ];

  const getImageSrc = (originalSrc: string, index: number) => {
    return imageError[originalSrc] ? fallbackImages[index] || fallbackImages[0] : originalSrc;
  };

  const handleImageError = (originalSrc: string) => {
    setImageError(prev => ({ ...prev, [originalSrc]: true }));
  };

  const openLightbox = (imageUrl: string, index: number) => {
    setSelectedImage(imageUrl);
    setCurrentImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % villaImages.length;
    setCurrentImageIndex(nextIndex);
    const nextImageSrc = getImageSrc(villaImages[nextIndex], nextIndex);
    setSelectedImage(nextImageSrc);
  };

  const prevImage = () => {
    const prevIndex = (currentImageIndex - 1 + villaImages.length) % villaImages.length;
    setCurrentImageIndex(prevIndex);
    const prevImageSrc = getImageSrc(villaImages[prevIndex], prevIndex);
    setSelectedImage(prevImageSrc);
  };

  return (
    <div className="min-h-screen bg-primary-50">
    <Helmet>
      <title>Villa DINOOS - Luxury Villa in Weligama, Sri Lanka</title>
      <meta
        name="description"
        content="Villa DINOOS offers luxury villas in Weligama, Sri Lanka with stunning ocean views, private pool, and premium amenities. Book your dream holiday today!"
      />
      <meta name="keywords" content="villa in Weligama, luxury villa Sri Lanka, holiday villa, private pool villa, beachfront villa" />
      <link rel="canonical" href="https://villadinoos.lk/villa" />

      {/* JSON-LD structured data for SEO */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "LodgingBusiness",
            "name": "Villa DINOOS",
            "description": "Luxury villa in Weligama, Sri Lanka with private pool, premium amenities, and stunning nature views.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Weligama",
              "addressCountry": "LK"
            },
            "telephone": "+94 777 336 354",
            "url": "https://villadinoos.lk/villa"
          }
        `}
      </script>
    </Helmet>
    
      {/* Hero Section */}
      <section className="relative h-96 bg-earth-brown">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getImageSrc(villaImages[0], 0)}')`
          }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex items-center justify-center h-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">The Villa</h1>
            <p className="text-xl md:text-2xl">Luxury meets nature in perfect harmony</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Description */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-serif font-semibold text-earth-brown">
                A Sanctuary of Comfort
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Villa DINOOS is a stunning retreat nestled in the heart of nature, offering the perfect 
                blend of luxury and tranquility. Our beautifully designed villa features four spacious 
                bedrooms, three full bathrooms, and an open-concept living area that seamlessly connects 
                indoor and outdoor living.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The property sits on 0.3 acres of landscaped grounds, complete with mature trees, walking 
                paths, and a private garden that provides the ultimate in privacy and serenity. Whether 
                you're looking for a romantic getaway, family vacation, or corporate retreat, Villa 
                DINOOS offers an unforgettable experience.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Every detail has been carefully considered to ensure your comfort, from the premium 
                bedding and luxurious bathrooms to the gourmet kitchen equipped with top-of-the-line 
                appliances. The living spaces are designed to feel both elegant and welcoming, with 
                warm earth tones and natural materials that reflect the surrounding landscape.
              </p>
              
              {/* Olive accent section */}
              <div className="bg-olive-50 border-l-4 border-olive-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-semibold text-olive-800 mb-2">Eco-Friendly Features</h3>
                <p className="text-olive-700 text-sm">
                  Villa DINOOS is committed to sustainability with organic gardens and eco-friendly practices for our guests.
                </p>
              </div>
            </motion.div>

            {/* Amenities */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h3 className="text-2xl font-serif font-semibold text-earth-brown mb-6">
                Amenities & Features
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-earth-beige rounded-lg flex items-center justify-center">
                      <amenity.icon className="w-5 h-5 text-earth-brown" />
                    </div>
                    <span className="text-gray-700">{amenity.text}</span>
                  </div>
                ))}
              </div>
              
              {/* Additional amenities with olive accent */}
              <div className="mt-8 p-4 bg-olive-50 rounded-lg border border-olive-200">
                <h4 className="font-semibold text-olive-800 mb-2">Premium Inclusions</h4>
                <ul className="text-sm text-olive-700 space-y-1">
                  <li>• Complimentary welcome basket with local treats</li>
                  <li>• 24/7 concierge service</li>
                  <li>• Daily housekeeping available</li>
                  <li>• Private chef services upon request</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-earth-brown mb-4">
              Photo Gallery
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore every corner of Villa DINOOS through our comprehensive photo gallery
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {villaImages
            .filter(image => image && image.trim() !== '') // only valid images
            .map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative overflow-hidden rounded-lg shadow-md group cursor-pointer"
                onClick={() => openLightbox(getImageSrc(image, index), index)}
              >
                <img 
                  src={getImageSrc(image, index)}
                  alt={`Villa gallery ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={() => handleImageError(image)}
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                <div className="absolute bottom-2 left-2 bg-olive-600 text-white px-2 py-1 rounded text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View Full Size
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-10"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 z-10"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          
          <img
            src={selectedImage}
            alt="Villa gallery"
            className="max-w-full max-h-full object-contain"
          />
          
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
            {currentImageIndex + 1} of {villaImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Villa;